import {
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  Alert
} from "@mui/material";
import { useState, useEffect } from "react";
import { BaseDialog } from "./base";
import { Checkboxes, Sliders, CheckboxToggle } from "components";
import { useOptions } from "hooks";
import { hhmmToMinutes, minutesToHHMM } from "utilities";

// Constants
const DEFAULT_FILTERS = {
  categories: [],
  dietaryPreferences: [],
  isTakeaway: false,
  isHappeningNow: false,
  priceMinimum: 0,
  priceMaximum: 1000,
  availableTimeMinimum: "00:00",
  availableTimeMaximum: "23:59"
};

const QUICK_FILTER_OPTIONS = [
  { key: 'isTakeaway', label: 'Takeaway Only' },
  { key: 'isHappeningNow', label: 'Happening Now' }
];

export function FilterDialog({
  open,
  title,
  filters = {},
  onFiltersChange,
  onClose,
}) {
  // Fetch filter options from Firebase
  const { categories, dietaryPreferences, loading: optionsLoading, error } = useOptions();

  // Local state for temporary filter selections
  const [tempFilters, setTempFilters] = useState(DEFAULT_FILTERS);
  const [validationError, setValidationError] = useState("");

  // Initialize temp filters when dialog opens or filters change
  useEffect(() => {
    if (open) {
      setTempFilters({
        ...DEFAULT_FILTERS,
        ...filters
      });
      setValidationError("");
    }
  }, [open, filters]);

  // Validation for slider ranges
  const validateFilters = (filters) => {
    if (filters.priceMinimum >= filters.priceMaximum) {
      return "Minimum price must be less than maximum price";
    }
    
    const minMinutes = hhmmToMinutes(filters.availableTimeMinimum);
    const maxMinutes = hhmmToMinutes(filters.availableTimeMaximum);
    if (minMinutes >= maxMinutes) {
      return "Start time must be before end time";
    }
    
    return "";
  };

  const handleSetTempFilters = (newFilters) => {
    const updatedFilters = {
      ...tempFilters,
      ...newFilters
    };
    
    const error = validateFilters(updatedFilters);
    setValidationError(error);
    setTempFilters(updatedFilters);
  };

  const handlePriceChange = (newPriceRange) => {
    const updatedFilters = {
      ...tempFilters,
      priceMinimum: newPriceRange[0],
      priceMaximum: newPriceRange[1]
    };
    
    const error = validateFilters(updatedFilters);
    setValidationError(error);
    setTempFilters(updatedFilters);
  };

  const handleTimeChange = (newTimeRange) => {
    const updatedFilters = {
      ...tempFilters,
      availableTimeMinimum: minutesToHHMM(newTimeRange[0]),
      availableTimeMaximum: minutesToHHMM(newTimeRange[1])
    };
    
    const error = validateFilters(updatedFilters);
    setValidationError(error);
    setTempFilters(updatedFilters);
  };

  const handleApplyFilters = () => {
    const error = validateFilters(tempFilters);
    if (error) {
      setValidationError(error);
      return;
    }
    
    onFiltersChange(tempFilters);
    onClose();
  };

  const handleClearFilters = () => {
    setTempFilters(DEFAULT_FILTERS);
    setValidationError("");
    onFiltersChange(DEFAULT_FILTERS);
  };

  if (error) {
    console.error('Error loading filter options:', error);
  }

  return (
    <BaseDialog 
      open={open} 
      onClose={onClose} 
      title={title} 
      width="100%" 
    >
      <DialogContent sx={{ pb: 2 }}>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}
        
        <Box sx={{ width: '100%', minWidth: 300 }}>
          <CheckboxToggle
            label="Availibility"
            options={QUICK_FILTER_OPTIONS}
            state={tempFilters}
            setState={setTempFilters}
          />

          {optionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Checkboxes
                title="Categories"
                property="categories"
                options={categories}
                state={tempFilters}
                setState={handleSetTempFilters}
              />

              <Checkboxes
                title="Dietary Preferences"
                property="dietaryPreferences"
                options={dietaryPreferences}
                state={tempFilters}
                setState={handleSetTempFilters}
              />

              <Sliders
                label="Price Range"
                value={[tempFilters.priceMinimum, tempFilters.priceMaximum]}
                onChange={handlePriceChange}
                min={0}
                max={1000}
                step={10}
                inputLabels={['Minimum', 'Maximum']}
                inputAdornment="R"
                formatValue={(value) => `R${value}`}
                parseValue={(value) => Number(value.replace('R', '').replace(/\s/g, ''))}
                showInputs={true}
              />

              <Sliders
                label="Available Time"
                value={[
                  hhmmToMinutes(tempFilters.availableTimeMinimum),
                  hhmmToMinutes(tempFilters.availableTimeMaximum)
                ]}
                onChange={handleTimeChange}
                min={0}
                max={1439}
                step={15}
                formatValue={minutesToHHMM}
                showInputs={false}
                state={tempFilters}
                setState={setTempFilters}
              />
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button
          onClick={handleClearFilters}
          variant="outlined"
          fullWidth
        >
          Clear
        </Button>
        <Button
          onClick={handleApplyFilters}
          variant="contained"
          fullWidth
        >
          Apply
        </Button>
      </DialogActions>
    </BaseDialog>
  );
}